import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 0.7rem;
  flex-wrap: wrap;
  align-items: center;
`;

const Input = styled.input`
  flex: 1 1 260px;
  min-width: 0;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0f172a;
  padding: 0.65rem 0.8rem;
`;

const Button = styled.button`
  flex-shrink: 0;
  border-radius: 10px;
  border: none;
  padding: 0.65rem 0.9rem;
  color: #fff;
  font-weight: 700;
  background: #2563eb;
  cursor: pointer;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;

  th,
  td {
    text-align: left;
    padding: 0.7rem;
    border-bottom: 1px solid #e2e8f0;
    font-size: 0.9rem;
    white-space: nowrap;
    color: #1f2937;
  }

  th {
    background: #f8fafc;
    color: #64748b;
    font-weight: 700;
    font-size: 0.8rem;
  }

  @media (max-width: 1200px) {
    min-width: 760px;

    th,
    td {
      padding: 0.6rem 0.55rem;
      font-size: 0.88rem;
    }

    .col-grade {
      display: none;
    }
  }

  @media (max-width: 980px) {
    min-width: 620px;

    .col-status,
    .col-checked-at,
    .col-end-time {
      display: none;
    }
  }

  @media (max-width: 640px) {
    min-width: 520px;

    th,
    td {
      padding: 0.52rem 0.45rem;
      font-size: 0.82rem;
    }
  }

  @media (max-width: 540px) {
    min-width: 420px;

    .col-duration,
    .col-checkout {
      display: none;
    }
  }
`;

const TableContainer = styled.div`
  margin-top: 0.95rem;
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 0.9rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
`;

const BlinkingRow = styled.tr`
  background: ${({ $overdue }) => ($overdue ? 'rgba(239, 68, 68, 0.15)' : 'transparent')};
  animation: ${({ $overdue }) => ($overdue ? 'dangerPulse 1s infinite' : 'none')};

  @keyframes dangerPulse {
    0% { background: rgba(239, 68, 68, 0.15); }
    50% { background: rgba(239, 68, 68, 0.35); }
    100% { background: rgba(239, 68, 68, 0.15); }
  }
`;

const Empty = styled.p`
  margin: 1.2rem 0 0;
  color: #64748b;
`;

const Message = styled.p`
  margin: 0.5rem 0 0;
  color: ${({ $error }) => ($error ? '#b91c1c' : '#1d4ed8')};
  font-size: 0.9rem;
`;

const DurationSelect = styled.select`
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0f172a;
  padding: 0.38rem 0.45rem;
`;

const DateInput = styled(Input)`
  flex: 0 0 180px;

  @media (max-width: 768px) {
    width: 100%;
    flex: 1 1 100%;
  }
`;

const RemainingTime = styled.span`
  color: ${({ $isUrgent, $isVeryUrgent, $isOverdue }) => {
    if ($isOverdue) return '#fda4af';
    if ($isVeryUrgent) return '#ef4444';
    if ($isUrgent) return '#fbbf24';
    return '#334155';
  }};
  font-weight: ${({ $isUrgent, $isVeryUrgent, $isOverdue }) =>
    $isUrgent || $isVeryUrgent || $isOverdue ? 700 : 500};
`;

const CheckoutCell = styled.td`
  white-space: nowrap;
`;

const CheckoutButton = styled.button`
  border-radius: 8px;
  border: none;
  padding: 0.45rem 0.65rem;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  color: #fff;
  background: linear-gradient(135deg, #10b981, #059669);

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const CheckoutDone = styled.span`
  color: #059669;
  font-size: 0.82rem;
  font-weight: 600;
`;

const formatDate = (iso) =>
  new Date(iso).toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const formatTime = (date) =>
  new Date(date).toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

const getEndTime = (checkedAt, durationHours) => {
  const startMs = new Date(checkedAt).getTime();
  const addMs = Number(durationHours) * 60 * 60 * 1000;
  return new Date(startMs + addMs);
};

const getStatusLabel = (checkedAt, durationHours, now) => {
  const start = new Date(checkedAt);
  const elapsedMin = Math.max(0, Math.floor((now.getTime() - start.getTime()) / 60000));
  const targetMin = Number(durationHours) * 60;
  if (elapsedMin > targetMin) {
    return `${elapsedMin}분 경과 (+${elapsedMin - targetMin}분)`;
  }
  return `${elapsedMin}분 경과 / ${targetMin}분`;
};

const getRemainingMinutes = (checkedAt, durationHours, now) => {
  const remainMs = getEndTime(checkedAt, durationHours).getTime() - now.getTime();
  return Math.ceil(remainMs / 60000);
};

const formatRemainingLabel = (remainMinutes) => {
  if (remainMinutes < 0) return `${Math.abs(remainMinutes)}분 초과`;
  if (remainMinutes === 0) return '곧 종료';
  return `${remainMinutes}분 남음`;
};

const toDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const seoulDateFormatter = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const toDateKeyFromIso = (iso) => {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (!Number.isNaN(parsed.getTime())) {
    return seoulDateFormatter.format(parsed);
  }
  if (typeof iso === 'string') {
    const matched = iso.match(/^(\d{4}-\d{2}-\d{2})/);
    if (matched) return matched[1];
  }
  return '';
};

/** Aligns API values with DurationSelect option values ("0.5"|"1"|"2"). */
const normalizeDurationOption = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return '2';
  if (Math.abs(n - 0.5) < 1e-6) return '0.5';
  if (Math.abs(n - 1) < 1e-6) return '1';
  if (Math.abs(n - 2) < 1e-6) return '2';
  return '2';
};

const AttendanceBoardPage = () => {
  const [message, setMessage] = useState({ text: '', error: false });
  const [keyword, setKeyword] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => toDateKey(new Date()));
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [durationByRecord, setDurationByRecord] = useState({});
  const [durationSavingId, setDurationSavingId] = useState(null);
  const [checkoutLoadingId, setCheckoutLoadingId] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 10000);
    return () => clearInterval(timer);
  }, []);

  const loadRecords = useCallback(async () => {
    try {
      setLoadingRecords(true);
      const response = await fetch(
        `${backendUrl}/api/attendance/records?date=${encodeURIComponent(selectedDate)}`
      );
      if (!response.ok) {
        throw new Error('출결 기록 요청 실패');
      }
      const payload = await response.json();
      const nextRecords = Array.isArray(payload.records) ? payload.records : [];
      const recordsBySelectedDate = nextRecords.filter(
        (record) => toDateKeyFromIso(record.checkedAt) === selectedDate
      );
      setRecords(recordsBySelectedDate);
      setDurationByRecord(
        recordsBySelectedDate.reduce((acc, record) => {
          acc[record.id] = normalizeDurationOption(
            record.durationHours != null ? record.durationHours : 2,
          );
          return acc;
        }, {})
      );
    } catch (error) {
      setMessage({ text: '출결 기록을 불러오지 못했습니다. 백엔드를 확인해주세요.', error: true });
    } finally {
      setLoadingRecords(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const filteredRecords = useMemo(() => {
    const keywordLower = keyword.toLowerCase();
    const searched = records.filter((record) =>
      `${record.studentName} ${record.studentEnglishName || ''} ${record.studentId} ${record.classroom}`
        .toLowerCase()
        .includes(keywordLower)
    );

    return [...searched].sort((a, b) => {
      const aDone = Boolean(a.checkedOutAt);
      const bDone = Boolean(b.checkedOutAt);
      if (aDone !== bDone) {
        return aDone ? 1 : -1;
      }
      const durationA = Number(durationByRecord[a.id] || '2');
      const durationB = Number(durationByRecord[b.id] || '2');
      const remainA = getEndTime(a.checkedAt, durationA).getTime() - now.getTime();
      const remainB = getEndTime(b.checkedAt, durationB).getTime() - now.getTime();
      return remainA - remainB;
    });
  }, [records, keyword, durationByRecord, now]);

  const handleCheckout = async (recordId) => {
    try {
      setCheckoutLoadingId(recordId);
      setMessage({ text: '', error: false });
      const response = await fetch(`${backendUrl}/api/attendance/records/${recordId}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status !== 'ok') {
        throw new Error(payload.message || '퇴실 처리에 실패했습니다.');
      }
      setRecords((prev) =>
        prev.map((r) =>
          r.id === recordId
            ? {
                ...r,
                checkedOutAt: payload.checkedOutAt,
                status: payload.attendanceStatus || '퇴실',
              }
            : r
        )
      );
      setMessage({ text: '퇴실 처리되었습니다.', error: false });
    } catch (error) {
      setMessage({ text: error.message || '퇴실 처리에 실패했습니다.', error: true });
    } finally {
      setCheckoutLoadingId(null);
    }
  };

  const handleDurationChange = async (recordId, nextDuration) => {
    const previousDuration = durationByRecord[recordId] || '2';
    setDurationByRecord((prev) => ({ ...prev, [recordId]: nextDuration }));
    try {
      setDurationSavingId(recordId);
      const response = await fetch(`${backendUrl}/api/attendance/records/${recordId}/duration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ durationHours: Number(nextDuration) }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload.status !== 'ok') {
        throw new Error(payload.message || '수업시간 저장에 실패했습니다.');
      }
      setDurationByRecord((prev) => ({
        ...prev,
        [recordId]: normalizeDurationOption(
          payload.durationHours != null ? payload.durationHours : nextDuration,
        ),
      }));
    } catch (error) {
      setDurationByRecord((prev) => ({ ...prev, [recordId]: previousDuration }));
      setMessage({ text: error.message || '수업시간 저장에 실패했습니다.', error: true });
    } finally {
      setDurationSavingId(null);
    }
  };

  return (
    <Wrapper>
      <SectionHeading
        eyebrow="STUDENT ATTENDANCE"
        title="입실 기록 관리"
        description="태블릿에서 입력된 입실 로그를 조회하는 화면입니다. 선택한 일자 기준으로 기록을 보여주며, 시간 경과 학생은 빨간색으로 깜빡이며 표시됩니다."
      />
          <Row>
            <Input
              type="text"
              placeholder="한글명/영문명/학년 검색"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
            />
            <DateInput
              type="date"
              aria-label="조회 일자"
              value={selectedDate}
              onChange={(event) => setSelectedDate(event.target.value)}
            />
            <Button
              type="button"
              onClick={() => {
                setNow(new Date());
                loadRecords();
              }}
            >
              새로고침
            </Button>
          </Row>
          {message.text ? <Message $error={message.error}>{message.text}</Message> : null}
          {loadingRecords ? <Empty>출결 기록을 불러오는 중입니다...</Empty> : null}
          {filteredRecords.length === 0 ? (
            <Empty>선택한 날짜 기록이 없습니다.</Empty>
          ) : (
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <th className="col-name">학생한글명</th>
                    <th className="col-english">학생영어닉네임</th>
                    <th className="col-grade">학년</th>
                    <th className="col-status">상태</th>
                    <th className="col-checked-at">시간</th>
                    <th className="col-duration">수업소요시간</th>
                    <th className="col-end-time">종료예정시간</th>
                    <th className="col-remaining">남은시간</th>
                    <th className="col-checkout">퇴실</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => {
                    const checkedOut = Boolean(record.checkedOutAt);
                    const selectedDuration = durationByRecord[record.id] || '2';
                    const endTime = getEndTime(record.checkedAt, selectedDuration);
                    const statusLabel = checkedOut
                      ? '퇴실'
                      : getStatusLabel(record.checkedAt, selectedDuration, now);
                    const targetMin = Number(selectedDuration) * 60;
                    const elapsedMin = Math.max(
                      0,
                      Math.floor((now.getTime() - new Date(record.checkedAt).getTime()) / 60000)
                    );
                    const isOverdue = !checkedOut && elapsedMin > targetMin;
                    const remainMinutes = getRemainingMinutes(record.checkedAt, selectedDuration, now);
                    const isVeryUrgent =
                      !checkedOut && remainMinutes >= 0 && remainMinutes <= 5;
                    const isUrgent =
                      !checkedOut && remainMinutes >= 0 && remainMinutes <= 10 && !isVeryUrgent;
                    return (
                      <BlinkingRow key={record.id} $overdue={isOverdue}>
                        <td className="col-name">{record.studentName}</td>
                        <td className="col-english">{record.studentEnglishName || '-'}</td>
                        <td className="col-grade">{record.classroom}</td>
                        <td className="col-status">{statusLabel}</td>
                        <td className="col-checked-at">{formatDate(record.checkedAt)}</td>
                        <td className="col-duration">
                          <DurationSelect
                            value={selectedDuration}
                            disabled={checkedOut || durationSavingId === record.id}
                            onChange={(event) => handleDurationChange(record.id, event.target.value)}
                          >
                            <option value="0.5">30분</option>
                            <option value="1">1시간</option>
                            <option value="2">2시간</option>
                          </DurationSelect>
                        </td>
                        <td className="col-end-time">{checkedOut ? '—' : formatTime(endTime)}</td>
                        <td className="col-remaining">
                          {checkedOut ? (
                            <CheckoutDone>—</CheckoutDone>
                          ) : (
                            <RemainingTime
                              $isUrgent={isUrgent}
                              $isVeryUrgent={isVeryUrgent}
                              $isOverdue={isOverdue}
                            >
                              {formatRemainingLabel(remainMinutes)}
                            </RemainingTime>
                          )}
                        </td>
                        <CheckoutCell className="col-checkout">
                          {checkedOut ? (
                            <CheckoutDone>{formatDate(record.checkedOutAt)}</CheckoutDone>
                          ) : (
                            <CheckoutButton
                              type="button"
                              disabled={checkoutLoadingId === record.id}
                              onClick={() => handleCheckout(record.id)}
                            >
                              {checkoutLoadingId === record.id ? '처리 중…' : '퇴실'}
                            </CheckoutButton>
                          )}
                        </CheckoutCell>
                      </BlinkingRow>
                    );
                  })}
                </tbody>
              </Table>
            </TableContainer>
          )}
    </Wrapper>
  );
};

export default AttendanceBoardPage;
